import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { brand, layout, shadows, textures } from '../theme';
import Img from '../components/Img';
import { gsap, useGsap, QUERIES } from '../animation/gsap';
import { useCompany, useGallery } from '../content/ContentProvider';
import { featuredPhotos } from '../api/content';
import Wordmark from '../components/Wordmark';

/**
 * The home page hero.
 *
 * On a desktop this pins for a screen and a half. The headline lands character
 * by character on load, then as you scroll the mosaic of tiles breaks apart and
 * drifts outward while the copy lifts away and the background darkens.
 *
 * On a phone the pin is dropped entirely. Pinning fights with the address bar
 * showing and hiding as you scroll, which makes the whole section jitter, and
 * it is the one place where a long scrub is most likely to feel broken rather
 * than impressive. The tiles still drift, just without holding the screen.
 */
export default function HeroPinned() {
	const company = useCompany();
	const gallery = useGallery();
	const tiles = featuredPhotos(gallery, 4);

	const root = useGsap(
		({ mm }) => {
			const q = gsap.utils.selector(root);

			mm.add(QUERIES.motion, () => {
				// The entrance. This runs on load, not on scroll. The headline is
				// the wordmark, whose coloured spans and gold dashes SplitText
				// would tear apart — so it arrives line by line instead of
				// character by character.
				const intro = gsap.timeline({ defaults: { ease: 'power3.out' } });

				intro
					.from(q('[data-eyebrow]'), { autoAlpha: 0, y: 20, duration: 0.6 })
					.from(
						q('[data-wm-line]'),
						{ autoAlpha: 0, y: 34, duration: 0.8, stagger: 0.14 },
						'-=0.3'
					)
					.from(q('[data-sub]'), { autoAlpha: 0, y: 20, duration: 0.6 }, '-=0.5')
					.from(q('[data-actions] > *'), { autoAlpha: 0, y: 20, duration: 0.5, stagger: 0.1 }, '-=0.35')
					.from(
						q('[data-tile]'),
						{ autoAlpha: 0, y: 60, scale: 0.9, duration: 1, stagger: 0.12 },
						0.2
					)
					.from(q('[data-scroll-hint]'), { autoAlpha: 0, duration: 0.6 }, '-=0.2');

				return () => intro.kill();
			});

			// The scroll performance, desktop only.
			mm.add(QUERIES.desktop, () => {
				const timeline = gsap.timeline({
					scrollTrigger: {
						trigger: root.current,
						start: 'top top',
						end: '+=140%',
						scrub: 1,
						pin: true,
						// Stops the one-frame jump some browsers show at the moment a
						// pin engages.
						anticipatePin: 1,
					},
				});

				/*
				 * Each tile leaves in its own direction, so the group comes apart
				 * rather than sliding as a block.
				 *
				 * The property split with the intro tween is deliberate: the intro
				 * owns y, scale, and opacity; this scrub owns xPercent, yPercent,
				 * and rotation. Neither touches the other's set. Scrolling before
				 * the intro finished used to have both tweens writing scale and
				 * rotation to the same tiles on the same frames, and the mosaic
				 * visibly stuttered as they fought. Disjoint properties compose.
				 */
				const drift = [
					{ xPercent: -70, yPercent: -30, rotateZ: -14 },
					{ xPercent: 65, yPercent: -18, rotateZ: 10 },
					{ xPercent: -50, yPercent: 45, rotateZ: 8 },
					{ xPercent: 80, yPercent: 40, rotateZ: -12 },
				];

				q('[data-tile]').forEach((tile, index) => {
					timeline.to(tile, { ...drift[index % drift.length], ease: 'none' }, 0);
				});

				timeline
					.to(q('[data-copy]'), { yPercent: -35, autoAlpha: 0, ease: 'none' }, 0)
					.to(q('[data-veil]'), { opacity: 0.9, ease: 'none' }, 0)
					// The grout grid drifts against the scroll, so the texture is
					// something the page moves through rather than a flat backdrop.
					.to(q('[data-grid]'), { yPercent: 10, ease: 'none' }, 0)
					.to(q('[data-scroll-hint]'), { autoAlpha: 0, duration: 0.15 }, 0);
			});

			// Phones get a gentle parallax instead of a pin — the photos and the
			// grout grid drift at different rates, which is all the depth a small
			// screen needs.
			mm.add(QUERIES.mobile, () => {
				const scrollTrigger = {
					trigger: root.current,
					start: 'top top',
					end: 'bottom top',
					scrub: 1,
				};

				gsap.to(q('[data-mosaic]'), { yPercent: -12, ease: 'none', scrollTrigger });
				gsap.to(q('[data-grid]'), { yPercent: 8, ease: 'none', scrollTrigger });
			});
		},
		[tiles.length]
	);

	return (
		<Box
			ref={root}
			component="section"
			sx={{
				position: 'relative',
				minHeight: { xs: 'auto', lg: '100vh' },
				display: 'flex',
				alignItems: 'center',
				overflow: 'hidden',
				/*
				 * Bright and warm, exactly as the PHP theme draws it: white falling
				 * into the brand's light grey, with a soft gold glow behind the
				 * mosaic. The dark sections further down provide the contrast; the
				 * first screen is optimistic.
				 */
				background: textures.heroGround,
				color: brand.text,
				pt: {
					xs: `calc(${layout.headerHeight} + 2rem)`,
					sm: `calc(${layout.headerHeightWide} + 2rem)`,
					lg: layout.headerHeightWide,
				},
				pb: { xs: 8, lg: 0 },
			}}
		>
			{/* The faint grout grid behind everything — the tile setter's texture.
			    Oversized so the scroll drift never exposes an edge. */}
			<Box
				data-grid
				aria-hidden="true"
				sx={{
					position: 'absolute',
					inset: '-12% 0',
					...textures.groutGrid(),
					maskImage: 'radial-gradient(ellipse at 30% 20%, #000 0%, transparent 75%)',
					pointerEvents: 'none',
				}}
			/>

			{/* The veil the scrub drives: the scene airs out into white as the
			    tiles scatter, instead of sinking into black. */}
			<Box
				data-veil
				sx={{
					position: 'absolute',
					inset: 0,
					zIndex: 2,
					pointerEvents: 'none',
					opacity: 0,
					background: `linear-gradient(180deg, ${brand.white} 0%, ${brand.surface} 100%)`,
				}}
			/>

			<Container sx={{ position: 'relative', zIndex: 3 }}>
				<Box
					sx={{
						display: 'grid',
						gap: { xs: 6, lg: 8 },
						gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
						alignItems: 'center',
						// Children may shrink below their content width. Without this
						// the wordmark's nowrap lines force the track wider than a
						// phone screen and shove the whole hero off to one side.
						'& > *': { minWidth: 0 },
					}}
				>
					{/* Centred on phones, left-anchored beside the mosaic on desktop. */}
					<Box data-copy sx={{ textAlign: { xs: 'center', lg: 'left' } }}>
						{/* The PHP theme's own eyebrow line, word for word. An earlier
						    draft said "Since day one" here, which is exactly the kind
						    of invented history docs/instructions.md forbids. */}
						<Typography
							data-eyebrow
							variant="overline"
							sx={{ display: 'block', color: brand.accentDark, mb: 2 }}
						>
							Tile and interior finishing in {company.city}, {company.region}
						</Typography>

						{/*
							The wordmark as the h1: the company name and slogan as real
							text, in the logo's own type. Sized so CONSTRUCTION runs
							about as wide as the paragraph below; everything inside
							scales from this one font-size.
						*/}
						<Wordmark
							component="h1"
							sx={{
								// The lockup is centred within itself, like the drawn logo.
								// The box hugs its content: centred on phones, on the
								// column's left edge on desktop.
								width: 'fit-content',
								maxWidth: '100%',
								mx: { xs: 'auto', lg: 0 },
								/*
								 * Scales hard with the viewport. The floor matters: at the
								 * old 1.05rem floor the nowrap CONSTRUCTION line came to
								 * ~400px on a 360px phone, wider than the screen, and the
								 * overflow pushed the whole page sideways — the "hero not
								 * centred" bug.
								 */
								fontSize: 'clamp(0.78rem, 0.45rem + 1.5vw, 1.7rem)',
								mb: 3,
							}}
						/>

						<Typography
							data-sub
							sx={{
								color: brand.textMuted,
								fontSize: '1.1rem',
								maxWidth: '46ch',
								mx: { xs: 'auto', lg: 0 },
								mb: 4,
							}}
						>
							Tile, flooring, drywall, painting, and finishing carpentry for homes across{' '}
							{company.service_area}. Prepared properly, finished cleanly, and left tidy.
						</Typography>

						<Stack
							data-actions
							direction={{ xs: 'column', sm: 'row' }}
							spacing={2}
							sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}
						>
							<Button variant="contained" color="primary" size="large" component={RouterLink} to="/contact">
								Get a free estimate
							</Button>
							<Button
								variant="outlined"
								size="large"
								href={company.phone_href}
								sx={{
									color: brand.dark,
									borderColor: brand.line,
									'&:hover': { borderColor: brand.accent, color: brand.accentDark },
								}}
							>
								{company.phone}
							</Button>
						</Stack>
					</Box>

					{/* The mosaic. */}
					<Box
						data-mosaic
						sx={{
							position: 'relative',
							display: 'grid',
							gridTemplateColumns: 'repeat(2, 1fr)',
							gap: 2,
							perspective: '1200px',
						}}
					>
						{tiles.map((photo, index) => (
							<Box
								key={photo.file}
								data-tile
								sx={{
									position: 'relative',
									borderRadius: 2,
									overflow: 'hidden',
									boxShadow: shadows.tile,
									aspectRatio: '3 / 4',
									// A slight stagger down the column so it reads as a stack
									// of samples rather than a grid of thumbnails.
									transform: index % 2 === 1 ? 'translateY(2.5rem)' : 'none',
									border: `1px solid ${brand.line}`,
								}}
							>
								<Img
									
									src={photo.src}
									srcSet={photo.srcset}
									sizes="(min-width: 62rem) 20rem, 45vw"
									alt={photo.alt}
									width={photo.width}
									height={photo.height}
									loading={index < 2 ? 'eager' : 'lazy'}
									/*
									 * Lowercase on purpose. React 18 does not know the
									 * camelCase `fetchPriority` — it warns and drops it,
									 * so the hint never reaches the browser. Unknown
									 * lowercase attributes are passed straight through,
									 * which is what actually gets it into the HTML.
									 * This can become `fetchPriority` on React 19.
									 */
									fetchpriority={index === 0 ? 'high' : undefined}
									sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
								/>
							</Box>
						))}
					</Box>
				</Box>
			</Container>

			{/* Scroll hint. */}
			<Box
				data-scroll-hint
				aria-hidden="true"
				sx={{
					position: 'absolute',
					bottom: '2rem',
					left: '50%',
					transform: 'translateX(-50%)',
					zIndex: 3,
					display: { xs: 'none', lg: 'flex' },
					flexDirection: 'column',
					alignItems: 'center',
					gap: 1,
					color: brand.textMuted,
					fontSize: '0.7rem',
					letterSpacing: '0.2em',
					textTransform: 'uppercase',
				}}
			>
				Scroll
				<Box
					sx={{
						width: '1px',
						height: '2.5rem',
						backgroundColor: brand.accent,
						'@media (prefers-reduced-motion: no-preference)': {
							animation: 'ttcScrollHint 1.8s ease-in-out infinite',
						},
						'@keyframes ttcScrollHint': {
							'0%, 100%': { opacity: 0.3, transform: 'scaleY(0.6)' },
							'50%': { opacity: 1, transform: 'scaleY(1)' },
						},
						transformOrigin: 'top',
					}}
				/>
			</Box>
		</Box>
	);
}
